import React, { forwardRef, useCallback, useEffect, useMemo } from 'react';

import {
  FormControl,
  FormHelperText,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';

import { EmptyArray } from '~/utils/arrayUtils';
import { isRequestPending, isRequestRejected, isRequestUnresolved } from '~/utils/requestUtils';

interface Option {
  id: string;
  name: string;
}

interface Props<T extends Option = Option> {
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  //   fetchOptions: () => FetchOptionsResult<T>;
  dataReq: Utils.RequestState<Array<T>, string, any>;
  refetch?: () => void;
}

export function parseTypeOptionFormData(formData: FormData, name: string) {
  return {
    [name]: String(formData.get(name)),
  } as Record<string, string>;
}

const ScheduleTypeOptionInput = forwardRef<HTMLFormElement, Props>((props, ref) => {
  const { name, label, value, onChange, dataReq } = props;
  const options = dataReq?.result ?? EmptyArray;

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  useEffect(() => {
    if (options.length && (!value || !options.find((i) => i.id === value))) {
      onChange(options[0].id);
    }
  }, [options, value, onChange]);

  const initialOption = useMemo<Option | undefined>(() => {
    if (value && !options.find((i) => i.id === value)) {
      return { id: value, name: value };
    }
  }, [options, value]);

  return (
    <Stack component="form" ref={ref}>
      <FormControl variant="standard" fullWidth>
        <InputLabel>{label}</InputLabel>
        <Select
          name={name}
          value={value}
          onChange={handleChange}
          required
          disabled={isRequestUnresolved(dataReq) || isRequestRejected(dataReq)}
          error={isRequestRejected(dataReq)}
        >
          {!!initialOption && (
            <MenuItem value={initialOption.id}>
              <i>{initialOption.name}</i>
            </MenuItem>
          )}
          {options.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
        {isRequestRejected(dataReq) && <FormHelperText error>{dataReq.error}</FormHelperText>}
        {isRequestPending(dataReq) && <LinearProgress sx={{ position: 'absolute' }} />}
      </FormControl>
    </Stack>
  );
});

ScheduleTypeOptionInput.displayName = 'ScheduleTypeOptionInput';
export default ScheduleTypeOptionInput;
